import {
  AccountInputs,
  Child529Plan,
  DebtObligation,
  FinancialPlanAccounts,
  FinancialPlanCurrentFinances,
  FinancialPlanData,
  FinancialPlanIncome,
  FinancialPlanProfile,
  FinancialPlanRetirement,
  IncomePhase,
  IncomePhaseMinimumResult,
  PhasedIncomeAffordability,
  RequiredIncomeResult,
  SwrIncomeRow,
  SwrMaximumIncome,
} from '../types/financialPlan';
import { ACCOUNT_DISPLAY_LABELS, grossIncomeAtAge, grossIncomeTodayAtAge, livingExpensesFromBudget, monthlyTithingAmount } from './financialPlanDefaults';
import {
  addLumpSumToAccountsByWeight,
  distributeExtraToAccounts,
} from './lgcAccountRouting';
import { accountMonthlyContributionsAtAge } from './lgcAccountAllocation';
import { applyRothIraCapToMonthlyContributions } from './lgcRothIra';

function clampNonNeg(n: number): number {
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function pctToRate(pct: number): number {
  return clampNonNeg(pct) / 100;
}

/** Future value of lump sum */
export function fvLumpSum(pv: number, ratePct: number, years: number): number {
  const r = pctToRate(ratePct);
  if (years <= 0) return clampNonNeg(pv);
  if (r === 0) return clampNonNeg(pv);
  return clampNonNeg(pv) * Math.pow(1 + r, years);
}

/** Future value of monthly contributions (annuity) */
export function fvAnnuityMonthly(pmt: number, ratePct: number, years: number): number {
  const r = pctToRate(ratePct) / 12;
  const n = Math.max(0, years * 12);
  if (n === 0) return 0;
  if (r === 0) return clampNonNeg(pmt) * n;
  return clampNonNeg(pmt) * ((Math.pow(1 + r, n) - 1) / r);
}

/** Payment needed per month to reach FV */
export function pmtMonthly(fv: number, ratePct: number, years: number): number {
  const r = pctToRate(ratePct) / 12;
  const n = Math.max(0, years * 12);
  if (n === 0 || fv <= 0) return 0;
  if (r === 0) return fv / n;
  return (fv * r) / (Math.pow(1 + r, n) - 1);
}

export function yearsToRetirement(currentAge: number, retirementAge: number): number {
  return Math.max(0, retirementAge - currentAge);
}

export function totalRetirementSpendingToday(retirement: FinancialPlanRetirement): number {
  if (retirement.spendingCategories?.length) {
    return retirement.spendingCategories.reduce((s, c) => s + clampNonNeg(c.annualAmountToday), 0);
  }
  return clampNonNeg(retirement.annualSpendingToday);
}

/** Monthly amount in retirement-year dollars for a category's today's annual spend */
export function retirementMonthlyFromTodayAnnual(
  annualToday: number,
  inflationPct: number,
  yearsToRetire: number,
): number {
  const inflation = pctToRate(inflationPct);
  const futureAnnual = clampNonNeg(annualToday) * Math.pow(1 + inflation, yearsToRetire);
  return futureAnnual / 12;
}

export function computeRetirementCorpus(
  profile: FinancialPlanData['profile'],
  retirement: FinancialPlanRetirement,
): number {
  const years = yearsToRetirement(profile.currentAge, profile.retirementAge);
  const inflation = pctToRate(profile.inflationPct);
  const swr = pctToRate(profile.safeWithdrawalRatePct) || 0.04;

  if (retirement.mode === 'corpus') {
    return clampNonNeg(retirement.targetCorpus);
  }

  const spendingToday = totalRetirementSpendingToday(retirement);
  const futureAnnualSpending = spendingToday * Math.pow(1 + inflation, years);
  return futureAnnualSpending / swr;
}

/** Inflated annual retirement spending at a given age (from today's category totals). */
export function annualRetirementSpendingAtAge(
  profile: FinancialPlanData['profile'],
  retirement: FinancialPlanRetirement,
  age: number,
): number {
  const spendingToday = totalRetirementSpendingToday(retirement);
  const yearsFromNow = Math.max(0, age - profile.currentAge);
  const inflation = pctToRate(profile.inflationPct);
  return clampNonNeg(spendingToday) * Math.pow(1 + inflation, yearsFromNow);
}

function swrIncomeFromBalance(
  balance: number,
  swrPct: number,
  inflationFactor: number,
): SwrIncomeRow {
  const swr = pctToRate(swrPct) || 0.04;
  const atRetirementAnnual = clampNonNeg(balance) * swr;
  const todayAnnual = inflationFactor > 0 ? atRetirementAnnual / inflationFactor : atRetirementAnnual;
  return {
    atRetirementAnnual,
    todayAnnual,
    atRetirementMonthly: atRetirementAnnual / 12,
    todayMonthly: todayAnnual / 12,
  };
}

export function computeSwrMaximumIncome(
  plan: FinancialPlanData,
  precomputed?: { retirementProjected: number; corpusNeeded: number },
): SwrMaximumIncome {
  const { profile } = plan;
  const years = yearsToRetirement(profile.currentAge, profile.retirementAge);
  const inflationFactor = Math.pow(1 + pctToRate(profile.inflationPct), years);
  const swrPct = profile.safeWithdrawalRatePct;

  const corpusNeeded = precomputed?.corpusNeeded ?? computeRetirementCorpus(profile, plan.retirement);
  let retirementProjected = precomputed?.retirementProjected;
  if (retirementProjected == null) {
    const returnPct = profile.portfolioReturnPct;
    retirementProjected = (Object.keys(plan.accounts) as (keyof FinancialPlanAccounts)[]).reduce(
      (s, key) => {
        const accountInput =
          key === 'k401' ? { ...plan.accounts[key], employerMatchPct: 0 } : plan.accounts[key];
        return s + computeAccountProjection(accountInput, years, returnPct);
      },
      0,
    );
  }

  const currentPath = swrIncomeFromBalance(retirementProjected, swrPct, inflationFactor);
  const goalPlan = swrIncomeFromBalance(corpusNeeded, swrPct, inflationFactor);

  return {
    swrPct,
    currentPath,
    goalPlan,
    gapAtRetirementMonthly: currentPath.atRetirementMonthly - goalPlan.atRetirementMonthly,
  };
}

export function computeAccountProjection(
  account: AccountInputs,
  years: number,
  defaultReturnPct: number,
): number {
  const rate = account.annualReturnPct ?? defaultReturnPct;
  const fromBalance = fvLumpSum(account.balance, rate, years);
  const fromContrib = fvAnnuityMonthly(account.monthlyContribution, rate, years);
  let matchBoost = 0;
  if (account.employerMatchPct != null && account.employerMatchPct > 0) {
    const matchMonthly = account.monthlyContribution * (account.employerMatchPct / 100);
    matchBoost = fvAnnuityMonthly(matchMonthly, rate, years);
  }
  return fromBalance + fromContrib + matchBoost;
}

export type CollegeNeedResult = {
  childId: string;
  childName: string;
  totalCost: number;
  monthlySavingsNeeded: number;
  projectedBalance: number;
};

export function computeCollegeNeeds(
  children: Child529Plan[],
  inflationPct: number,
  returnPct: number,
): CollegeNeedResult[] {
  const inflation = pctToRate(inflationPct);
  const rate = returnPct;

  return children.map((child) => {
    const yearsUntil = Math.max(0, child.collegeStartAge - child.currentAge);
    let totalCost = 0;
    for (let y = 0; y < child.yearsOfCollege; y++) {
      const yearCost =
        clampNonNeg(child.annualCost) * Math.pow(1 + inflation, yearsUntil + y);
      totalCost += yearCost;
    }

    const projectedBalance =
      fvLumpSum(child.balance529, rate, yearsUntil) +
      fvAnnuityMonthly(child.monthlyContribution529, rate, yearsUntil);

    const shortfall = Math.max(0, totalCost - projectedBalance);
    const monthlySavingsNeeded =
      yearsUntil > 0 ? pmtMonthly(shortfall, rate, yearsUntil) : shortfall;

    return {
      childId: child.id,
      childName: child.name,
      totalCost,
      monthlySavingsNeeded,
      projectedBalance,
    };
  });
}

export function childAgeAtPlanAge(
  child: Child529Plan,
  planCurrentAge: number,
  planAge: number,
): number {
  return child.currentAge + (planAge - planCurrentAge);
}

export { grossIncomeAtAge, grossIncomeTodayAtAge } from './financialPlanDefaults';

/** Monthly living (budget + tithing on income at age) for required-income math. */
export function livingMonthlyAtPlanAge(plan: FinancialPlanData, age: number): number {
  const { profile, currentFinances, income } = plan;
  const budget = livingExpensesFromBudget(currentFinances.budgetCategories);

  if (age === profile.currentAge) {
    return clampNonNeg(currentFinances.monthlyExpenses);
  }

  const grossNominal = grossIncomeAtAge(profile, income, age);
  return (
    budget +
    monthlyTithingAmount(grossNominal, profile.effectiveTaxRatePct, currentFinances.tithingPct)
  );
}

export type IncomePhaseValidation = {
  overlaps: Array<{ a: string; b: string }>;
  gaps: Array<{ fromAge: number; toAge: number }>;
};

export function validateIncomePhases(
  phases: IncomePhase[],
  currentAge: number,
  retirementAge: number,
): IncomePhaseValidation {
  const sorted = [...phases].sort((a, b) => a.startAge - b.startAge);
  const overlaps: Array<{ a: string; b: string }> = [];
  const gaps: Array<{ fromAge: number; toAge: number }> = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const cur = sorted[i];
    const next = sorted[i + 1];
    if (cur.endAge >= next.startAge) {
      overlaps.push({ a: cur.id, b: next.id });
    } else if (cur.endAge + 1 < next.startAge) {
      gaps.push({ fromAge: cur.endAge + 1, toAge: next.startAge - 1 });
    }
  }

  if (sorted.length > 0) {
    if (sorted[0].startAge > currentAge) {
      gaps.unshift({ fromAge: currentAge, toAge: sorted[0].startAge - 1 });
    }
    const last = sorted[sorted.length - 1];
    if (last.endAge < retirementAge - 1) {
      gaps.push({ fromAge: last.endAge + 1, toAge: retirementAge - 1 });
    }
  }

  return { overlaps, gaps };
}

export function amortizeDebtYear(
  balance: number,
  ratePct: number,
  monthlyPayment: number,
): { nextBalance: number; annualPayment: number; interestPaid: number; principalPaid: number } {
  let b = clampNonNeg(balance);
  if (b <= 0) {
    return { nextBalance: 0, annualPayment: 0, interestPaid: 0, principalPaid: 0 };
  }
  const r = pctToRate(ratePct) / 12;
  let interestPaid = 0;
  let principalPaid = 0;
  let annualPayment = 0;
  for (let m = 0; m < 12; m++) {
    const interest = b * r;
    const payment = Math.min(clampNonNeg(monthlyPayment), b + interest);
    interestPaid += interest;
    principalPaid += Math.max(0, payment - interest);
    b = Math.max(0, b + interest - payment);
    annualPayment += payment;
  }
  return { nextBalance: b, annualPayment, interestPaid, principalPaid };
}

export type DebtLedgerAtAge = {
  balances: Record<string, number>;
  monthlyService: number;
  annualService: number;
};

function simulateDebtBalancesToAge(
  debts: DebtObligation[],
  profile: FinancialPlanProfile,
  age: number,
): Record<string, number> {
  const balances: Record<string, number> = {};
  for (const debt of debts) {
    let b = clampNonNeg(debt.balance);
    const start = debt.startAge ?? profile.currentAge;
    for (let a = start; a < age; a++) {
      const result = amortizeDebtYear(b, debt.interestRatePct, debt.monthlyPayment);
      b = result.nextBalance;
    }
    balances[debt.id] = b;
  }
  return balances;
}

export function debtLedgerAtAge(
  debts: DebtObligation[],
  profile: FinancialPlanProfile,
  age: number,
): DebtLedgerAtAge {
  const balances = simulateDebtBalancesToAge(debts, profile, age);
  let monthlyService = 0;
  for (const debt of debts) {
    const start = debt.startAge ?? profile.currentAge;
    if (age >= start && (balances[debt.id] ?? 0) > 0) {
      monthlyService += clampNonNeg(debt.monthlyPayment);
    }
  }
  return { balances, monthlyService, annualService: monthlyService * 12 };
}

export function projectDebtPayoffAge(debt: DebtObligation, profile: FinancialPlanProfile): number | null {
  let b = clampNonNeg(debt.balance);
  if (b <= 0) return debt.startAge ?? profile.currentAge;
  const start = debt.startAge ?? profile.currentAge;
  const maxAge = profile.retirementAge + 50;
  for (let age = start; age <= maxAge; age++) {
    if (b <= 0) return age;
    const result = amortizeDebtYear(b, debt.interestRatePct, debt.monthlyPayment);
    b = result.nextBalance;
    if (b <= 0) return age;
  }
  return null;
}

export function k12AnnualCostAtAge(
  child: Child529Plan,
  profile: FinancialPlanProfile,
  planAge: number,
): number {
  const childAge = childAgeAtPlanAge(child, profile.currentAge, planAge);
  let costToday = 0;
  if (childAge >= child.primaryStartAge && childAge <= child.primaryEndAge) {
    costToday = clampNonNeg(child.primaryAnnualCostToday);
  } else if (childAge >= child.highSchoolStartAge && childAge <= child.highSchoolEndAge) {
    costToday = clampNonNeg(child.highSchoolAnnualCostToday);
  }
  if (costToday <= 0) return 0;
  const yearsFromNow = Math.max(0, planAge - profile.currentAge);
  return costToday * Math.pow(1 + pctToRate(profile.inflationPct), yearsFromNow);
}

export function k12SpendingAtAge(
  children: Child529Plan[],
  profile: FinancialPlanProfile,
  planAge: number,
): number {
  return children.reduce((s, c) => s + k12AnnualCostAtAge(c, profile, planAge), 0);
}

export type K12NeedResult = {
  childId: string;
  childName: string;
  totalCost: number;
  monthlySavingsNeeded: number;
};

export function computeK12Needs(
  children: Child529Plan[],
  profile: FinancialPlanProfile,
  returnPct: number,
): K12NeedResult[] {
  return children.map((child) => {
    let totalCost = 0;
    for (let planAge = profile.currentAge; planAge <= profile.retirementAge; planAge++) {
      totalCost += k12AnnualCostAtAge(child, profile, planAge);
    }
    const yearsUntilFirst = Math.max(0, child.primaryStartAge - child.currentAge);
    const monthlySavingsNeeded =
      totalCost > 0 && yearsUntilFirst > 0
        ? pmtMonthly(totalCost, returnPct, yearsUntilFirst)
        : totalCost > 0
          ? totalCost
          : 0;
    return {
      childId: child.id,
      childName: child.name,
      totalCost,
      monthlySavingsNeeded,
    };
  });
}

export type MonthlySavingsBundle = {
  retirement: number;
  college: number;
  k12: number;
  health: number;
  lifestyle: number;
};

export type YearCashflowAtAge = {
  requiredSaveMonthly: number;
  requiredSaveAnnual: number;
  requiredSpendAnnual: number;
  takeHomeAnnual: number;
  grossIncomeAnnual: number;
  grossIncomeToday: number;
  requiredGrossAnnual: number;
  surplusAnnual: number;
};

/** Required save/spend and actual income at a plan age (nominal dollars). */
export function computeYearCashflowAtAge(
  plan: FinancialPlanData,
  age: number,
  phase: 'accumulation' | 'drawdown',
  annualWithdrawal = 0,
): YearCashflowAtAge {
  const { profile, income } = plan;
  const taxRate = pctToRate(profile.effectiveTaxRatePct);

  if (phase === 'drawdown') {
    const withdrawal = Math.max(0, annualWithdrawal);
    return {
      requiredSaveMonthly: 0,
      requiredSaveAnnual: 0,
      requiredSpendAnnual: withdrawal,
      takeHomeAnnual: withdrawal,
      grossIncomeAnnual: 0,
      grossIncomeToday: 0,
      requiredGrossAnnual: 0,
      surplusAnnual: 0,
    };
  }

  const constant = computeConstantSavingsBundle(plan);
  const savings = savingsBundleAtAge(plan, constant, age);
  const requiredSaveMonthly =
    savings.retirement + savings.college + savings.k12 + savings.health + savings.lifestyle;
  const debtAnnual = debtLedgerAtAge(plan.debts, profile, age).annualService;
  const k12CashAnnual = k12SpendingAtAge(plan.children, profile, age);
  const livingAnnual = livingMonthlyAtPlanAge(plan, age) * 12;
  const requiredSpendAnnual = livingAnnual + debtAnnual + k12CashAnnual;
  const grossIncomeAnnual = grossIncomeAtAge(profile, income, age);
  const grossIncomeToday = grossIncomeTodayAtAge(profile, income, age);
  const takeHomeAnnual = grossIncomeAnnual * (1 - taxRate);
  const requiredGrossAnnual = requiredGrossAtAge(plan, age, savings);
  const surplusAnnual = takeHomeAnnual - requiredSpendAnnual - requiredSaveMonthly * 12;

  return {
    requiredSaveMonthly,
    requiredSaveAnnual: requiredSaveMonthly * 12,
    requiredSpendAnnual,
    takeHomeAnnual,
    grossIncomeAnnual,
    grossIncomeToday,
    requiredGrossAnnual,
    surplusAnnual,
  };
}

const INVESTMENT_ACCOUNT_KEYS: (keyof FinancialPlanAccounts)[] = ['k401', 'rothIra', 'hsa', 'taxable'];

function startingInvestmentBalances(accounts: FinancialPlanAccounts): Record<keyof FinancialPlanAccounts, number> {
  return {
    k401: clampNonNeg(accounts.k401.balance),
    rothIra: clampNonNeg(accounts.rothIra.balance),
    hsa: clampNonNeg(accounts.hsa.balance),
    taxable: clampNonNeg(accounts.taxable.balance),
  };
}

function mergeMonthlyContributions(
  base: Record<keyof FinancialPlanAccounts, number>,
  extra: Record<keyof FinancialPlanAccounts, number>,
): Record<keyof FinancialPlanAccounts, number> {
  const merged = {} as Record<keyof FinancialPlanAccounts, number>;
  INVESTMENT_ACCOUNT_KEYS.forEach((k) => {
    merged[k] = Math.max(0, base[k]) + Math.max(0, extra[k]);
  });
  return merged;
}

function advanceInvestmentAccountsOneYear(
  balances: Record<keyof FinancialPlanAccounts, number>,
  accounts: FinancialPlanAccounts,
  defaultReturnPct: number,
  extraByAccount: Record<keyof FinancialPlanAccounts, number>,
  baseMonthlyByAccount: Record<keyof FinancialPlanAccounts, number>,
  age: number,
): Record<keyof FinancialPlanAccounts, number> {
  const combined = applyRothIraCapToMonthlyContributions(
    mergeMonthlyContributions(baseMonthlyByAccount, extraByAccount),
    age,
  );
  const next = {} as Record<keyof FinancialPlanAccounts, number>;
  INVESTMENT_ACCOUNT_KEYS.forEach((k) => {
    const rate = accounts[k].annualReturnPct ?? defaultReturnPct;
    const monthlyContribution = combined[k];
    next[k] = computeAccountProjection(
      {
        balance: balances[k],
        monthlyContribution,
        annualReturnPct: rate,
        ...(k === 'k401' ? { employerMatchPct: 0 } : {}),
      },
      1,
      rate,
    );
  });
  return next;
}

function totalInvestmentBalance(balances: Record<keyof FinancialPlanAccounts, number>): number {
  return INVESTMENT_ACCOUNT_KEYS.reduce((s, k) => s + balances[k], 0);
}

/** Surplus take-home after living, debt service, and K-12 cash at a plan age. */
export function phasedSurplusTakeHomeAnnual(plan: FinancialPlanData, age: number): number {
  const { profile, income, children, debts } = plan;
  const taxRate = pctToRate(profile.effectiveTaxRatePct);
  const gross = grossIncomeAtAge(profile, income, age);
  const takeHome = gross * (1 - taxRate);
  const living = livingMonthlyAtPlanAge(plan, age) * 12;
  const debt = debtLedgerAtAge(debts, profile, age).annualService;
  const k12Cash = k12SpendingAtAge(children, profile, age);
  return Math.max(0, takeHome - living - debt - k12Cash);
}

function addLumpSumToInvestments(
  balances: Record<keyof FinancialPlanAccounts, number>,
  plan: FinancialPlanData,
  lumpSum: number,
  age: number,
  extraMonthly: Record<keyof FinancialPlanAccounts, number> = distributeExtraToAccounts(plan, 0),
): Record<keyof FinancialPlanAccounts, number> {
  const baseMonthly = accountMonthlyContributionsAtAge(plan, age);
  const cappedMonthly = applyRothIraCapToMonthlyContributions(
    mergeMonthlyContributions(baseMonthly, extraMonthly),
    age,
  );
  return addLumpSumToAccountsByWeight(balances, plan, lumpSum, {
    age,
    rothContributedAnnual: cappedMonthly.rothIra * 12,
  });
}

function projectInvestmentsWithBaseContribYears(
  balances: Record<keyof FinancialPlanAccounts, number>,
  plan: FinancialPlanData,
  startAge: number,
  years: number,
): number {
  const { accounts } = plan;
  const returnPct = plan.profile.portfolioReturnPct;
  const zeroExtra = distributeExtraToAccounts(plan, 0);
  let bal = balances;
  for (let i = 0; i < years; i++) {
    const baseMonthly = accountMonthlyContributionsAtAge(plan, startAge + i);
    bal = advanceInvestmentAccountsOneYear(bal, accounts, returnPct, zeroExtra, baseMonthly, startAge + i);
  }
  return totalInvestmentBalance(bal);
}

/** Remaining retirement corpus gap at an age given current investment balances. */
export function remainingRetirementGapAtAge(
  plan: FinancialPlanData,
  invBalances: Record<keyof FinancialPlanAccounts, number>,
  age: number,
): number {
  const { profile, accounts, retirement } = plan;
  const yearsLeft = profile.retirementAge - age;
  if (yearsLeft <= 0) return 0;
  const corpusNeeded = computeRetirementCorpus(profile, retirement);
  const projected = projectInvestmentsWithBaseContribYears(
    invBalances,
    plan,
    age,
    yearsLeft,
  );
  return Math.max(0, corpusNeeded - projected);
}

/** Phased-income surplus (take-home) routed to retirement at a plan age, capped at remaining gap. */
export function surplusLumpSumToRetirementAtAge(
  plan: FinancialPlanData,
  invBal: Record<keyof FinancialPlanAccounts, number>,
  age: number,
): number {
  const { profile, children } = plan;
  let surplus = phasedSurplusTakeHomeAnnual(plan, age);
  const k12Due = k12SpendingAtAge(children, profile, age);
  surplus = Math.max(0, surplus - Math.min(Math.max(0, surplus), k12Due));
  if (surplus <= 0) return 0;
  const retGap = remainingRetirementGapAtAge(plan, invBal, age);
  return Math.min(surplus, retGap);
}

/** Allocate phased-income surplus toward retirement investments, capped at remaining gap. */
export function applyOnPaceInvestmentSurplus(
  plan: FinancialPlanData,
  invBal: Record<keyof FinancialPlanAccounts, number>,
  age: number,
  onPaceExtraMonthly: Record<keyof FinancialPlanAccounts, number> = distributeExtraToAccounts(plan, 0),
): Record<keyof FinancialPlanAccounts, number> {
  const toRet = surplusLumpSumToRetirementAtAge(plan, invBal, age);
  if (toRet <= 0) return invBal;
  return addLumpSumToInvestments(invBal, plan, toRet, age, onPaceExtraMonthly);
}

function collegeTotalCost(child: Child529Plan, profile: FinancialPlanProfile): number {
  const yearsUntil = Math.max(0, child.collegeStartAge - child.currentAge);
  const inflation = pctToRate(profile.inflationPct);
  let totalCost = 0;
  for (let y = 0; y < child.yearsOfCollege; y++) {
    totalCost += clampNonNeg(child.annualCost) * Math.pow(1 + inflation, yearsUntil + y);
  }
  return totalCost;
}

function collegeStartPlanAge(child: Child529Plan, profile: FinancialPlanProfile): number {
  return profile.currentAge + Math.max(0, child.collegeStartAge - child.currentAge);
}

function project529WithBaseContribYears(
  balance: number,
  monthlyContribution: number,
  returnPct: number,
  years: number,
): number {
  let bal = balance;
  for (let i = 0; i < years; i++) {
    bal = computeAccountProjection(
      { balance: bal, monthlyContribution, annualReturnPct: returnPct },
      1,
      returnPct,
    );
  }
  return bal;
}

function remainingCollegeGapAtAge(
  plan: FinancialPlanData,
  child529: Record<string, number>,
  age: number,
): number {
  const { profile, children } = plan;
  const returnPct = profile.portfolioReturnPct;
  let gap = 0;
  for (const child of children) {
    const collegeStart = collegeStartPlanAge(child, profile);
    if (age >= collegeStart) continue;
    const yearsLeft = collegeStart - age;
    if (yearsLeft <= 0) continue;
    const need = collegeTotalCost(child, profile);
    const projected = project529WithBaseContribYears(
      child529[child.id] ?? child.balance529,
      child.monthlyContribution529,
      returnPct,
      yearsLeft,
    );
    gap += Math.max(0, need - projected);
  }
  return gap;
}

type PhasedSurplusProjection = {
  retirementAtRetire: number;
  college529AtStart: Record<string, number>;
  k12CoveredBySurplus: number;
};

/** Year-by-year phased surplus split across retirement, 529, and K-12 cash costs. */
function simulatePhasedSurplusAllocation(plan: FinancialPlanData): PhasedSurplusProjection {
  const { profile, accounts, children } = plan;
  const returnPct = profile.portfolioReturnPct;
  const zeroExtra = distributeExtraToAccounts(plan, 0);

  let invBal = startingInvestmentBalances(accounts);
  const child529: Record<string, number> = {};
  children.forEach((c) => {
    child529[c.id] = clampNonNeg(c.balance529);
  });
  const college529AtStart: Record<string, number> = {};
  let k12CoveredBySurplus = 0;

  for (let age = profile.currentAge; age < profile.retirementAge; age++) {
    const baseMonthly = accountMonthlyContributionsAtAge(plan, age);
    invBal = advanceInvestmentAccountsOneYear(invBal, accounts, returnPct, zeroExtra, baseMonthly, age);

    for (const child of children) {
      const collegeStart = collegeStartPlanAge(child, profile);
      if (age < collegeStart) {
        child529[child.id] = computeAccountProjection(
          {
            balance: child529[child.id] ?? 0,
            monthlyContribution: child.monthlyContribution529,
            annualReturnPct: returnPct,
          },
          1,
          returnPct,
        );
      }
      if (age === collegeStart) {
        college529AtStart[child.id] = child529[child.id] ?? 0;
      }
    }

    let surplus = phasedSurplusTakeHomeAnnual(plan, age);
    const k12Due = k12SpendingAtAge(children, profile, age);
    const k12Paid = Math.min(Math.max(0, surplus), k12Due);
    k12CoveredBySurplus += k12Paid;
    surplus = Math.max(0, surplus - k12Paid);

    if (surplus <= 0) continue;

    const retGap = remainingRetirementGapAtAge(plan, invBal, age);
    const colGap = remainingCollegeGapAtAge(plan, child529, age);
    const totalGap = retGap + colGap;

    let toRet = surplus;
    let toCollege = 0;
    if (totalGap > 0) {
      toRet = surplus * (retGap / totalGap);
      toCollege = surplus * (colGap / totalGap);
    }

    invBal = addLumpSumToInvestments(invBal, plan, toRet, age);

    const activeCollegeKids = children.filter((c) => age < collegeStartPlanAge(c, profile));
    if (toCollege > 0 && activeCollegeKids.length > 0) {
      const perChild = toCollege / activeCollegeKids.length;
      activeCollegeKids.forEach((c) => {
        child529[c.id] = (child529[c.id] ?? 0) + perChild;
      });
    }
  }

  children.forEach((child) => {
    if (college529AtStart[child.id] == null) {
      college529AtStart[child.id] = child529[child.id] ?? child.balance529;
    }
  });

  return {
    retirementAtRetire: totalInvestmentBalance(invBal),
    college529AtStart,
    k12CoveredBySurplus,
  };
}

function computePathAdjustedCollegeMonthly(
  plan: FinancialPlanData,
  fromAge: number,
  projection: PhasedSurplusProjection,
): number {
  const { profile, children } = plan;
  const returnPct = profile.portfolioReturnPct;
  let total = 0;
  for (const child of children) {
    const collegeStart = collegeStartPlanAge(child, profile);
    if (fromAge >= collegeStart) continue;
    const yearsUntil = collegeStart - fromAge;
    const need = collegeTotalCost(child, profile);
    const projected = projection.college529AtStart[child.id] ?? child.balance529;
    const shortfall = Math.max(0, need - projected);
    total += yearsUntil > 0 ? pmtMonthly(shortfall, returnPct, yearsUntil) : shortfall;
  }
  return total;
}

function computePathAdjustedK12Monthly(
  plan: FinancialPlanData,
  fromAge: number,
  constantK12: number,
  projection: PhasedSurplusProjection,
): number {
  if (constantK12 <= 0) return 0;
  const { profile, children } = plan;
  const returnPct = profile.portfolioReturnPct;
  const k12Details = computeK12Needs(children, profile, returnPct);
  const totalK12Need = k12Details.reduce((s, d) => s + d.totalCost, 0);
  const remaining = Math.max(0, totalK12Need - projection.k12CoveredBySurplus);
  if (remaining <= 0) return 0;

  let yearsToFirst = profile.retirementAge - fromAge;
  for (const child of children) {
    const yearsUntilPrimary = Math.max(0, child.primaryStartAge - child.currentAge - (fromAge - profile.currentAge));
    yearsToFirst = Math.min(yearsToFirst, Math.max(0, yearsUntilPrimary));
  }
  if (yearsToFirst <= 0) return remaining;
  return pmtMonthly(remaining, returnPct, yearsToFirst);
}

/** Savings still required at fromAge after crediting future phased-income surplus toward goals. */
function computePathAdjustedSavingsBundle(
  plan: FinancialPlanData,
  constant: MonthlySavingsBundle,
  fromAge: number,
): MonthlySavingsBundle {
  const { profile, income, retirement } = plan;
  if (income.phases.length === 0) {
    return constant;
  }

  const years = profile.retirementAge - fromAge;
  if (years <= 0) {
    return { retirement: 0, college: 0, k12: 0, health: 0, lifestyle: 0 };
  }

  const returnPct = profile.portfolioReturnPct;
  const corpusNeeded = computeRetirementCorpus(profile, retirement);
  const projection = simulatePhasedSurplusAllocation(plan);

  const retirementShortfall = Math.max(0, corpusNeeded - projection.retirementAtRetire);
  const pathRetirement =
    retirementShortfall > 0 ? pmtMonthly(retirementShortfall, returnPct, years) : 0;
  const pathCollege = computePathAdjustedCollegeMonthly(plan, fromAge, projection);
  const pathK12 = computePathAdjustedK12Monthly(plan, fromAge, constant.k12, projection);

  return {
    retirement: pathRetirement,
    college: pathCollege,
    k12: pathK12,
    health: constant.health,
    lifestyle: constant.lifestyle,
  };
}

function savingsBundleAtAge(
  plan: FinancialPlanData,
  constant: MonthlySavingsBundle,
  age: number,
): MonthlySavingsBundle {
  const phasedActive = plan.income.phases.length > 0;
  return phasedActive ? computePathAdjustedSavingsBundle(plan, constant, age) : constant;
}

function requiredGrossAtAge(
  plan: FinancialPlanData,
  age: number,
  savings: MonthlySavingsBundle,
): number {
  const taxRate = pctToRate(plan.profile.effectiveTaxRatePct);
  const livingMonthly = livingMonthlyAtPlanAge(plan, age);
  const debtMonthly = debtLedgerAtAge(plan.debts, plan.profile, age).monthlyService;
  const requiredNetMonthly =
    livingMonthly + savings.retirement + savings.college + savings.k12 + savings.health + savings.lifestyle + debtMonthly;
  return taxRate < 1 ? (requiredNetMonthly * 12) / (1 - taxRate) : requiredNetMonthly * 12;
}

function nominalToTodayAtAge(profile: FinancialPlanProfile, age: number, nominal: number): number {
  const yearsFromNow = Math.max(0, age - profile.currentAge);
  const inflation = pctToRate(profile.inflationPct);
  return yearsFromNow > 0 ? nominal / Math.pow(1 + inflation, yearsFromNow) : nominal;
}

function requiredGrossTodayAtAge(
  plan: FinancialPlanData,
  age: number,
  savings: MonthlySavingsBundle,
): number {
  return nominalToTodayAtAge(plan.profile, age, requiredGrossAtAge(plan, age, savings));
}

type PeakYearMetrics = {
  peakRequiredGross: number;
  worstYearGap: number;
  peakYearAge: number;
  worstGapAge: number;
  phasedAffordability?: PhasedIncomeAffordability;
};

function computePeakYearMetrics(
  plan: FinancialPlanData,
  savings: MonthlySavingsBundle,
  headlineRequiredGross: number,
): PeakYearMetrics {
  const { profile, income } = plan;
  const phasedActive = income.phases.length > 0;
  const useDynamic =
    phasedActive || plan.debts.some((d) => clampNonNeg(d.balance) > 0 && clampNonNeg(d.monthlyPayment) > 0);

  if (!useDynamic) {
    const gap = headlineRequiredGross - grossIncomeAtAge(profile, income, profile.currentAge);
    return {
      peakRequiredGross: headlineRequiredGross,
      worstYearGap: gap,
      peakYearAge: profile.currentAge,
      worstGapAge: profile.currentAge,
    };
  }

  let peakRequiredGross = 0;
  let worstYearGap = Number.NEGATIVE_INFINITY;
  let peakYearAge = profile.currentAge;
  let worstGapAge = profile.currentAge;
  let underfundedYearCount = 0;
  let bottleneckFundedPct = 100;
  const yearsModeled = Math.max(0, profile.retirementAge - profile.currentAge);

  for (let age = profile.currentAge; age < profile.retirementAge; age++) {
    const savingsAtAge = savingsBundleAtAge(plan, savings, age);
    const requiredGross = requiredGrossAtAge(plan, age, savingsAtAge);
    const incomeGross = grossIncomeAtAge(profile, income, age);
    const gap = requiredGross - incomeGross;
    if (requiredGross > peakRequiredGross) {
      peakRequiredGross = requiredGross;
      peakYearAge = age;
    }
    if (gap > worstYearGap) {
      worstYearGap = gap;
      worstGapAge = age;
    }
    if (gap > 0) underfundedYearCount += 1;
    if (requiredGross > 0) {
      bottleneckFundedPct = Math.min(bottleneckFundedPct, (incomeGross / requiredGross) * 100);
    } else if (incomeGross <= 0) {
      bottleneckFundedPct = 0;
    }
  }

  if (!Number.isFinite(worstYearGap)) worstYearGap = 0;

  const phasedAffordability: PhasedIncomeAffordability | undefined = phasedActive
    ? {
        active: true,
        planAchievable: underfundedYearCount === 0,
        underfundedYearCount,
        yearsModeled,
        worstGapAge,
        worstYearGap,
        worstYearIncomeToday: grossIncomeTodayAtAge(profile, income, worstGapAge),
        worstYearRequiredToday: requiredGrossTodayAtAge(plan, worstGapAge, savingsBundleAtAge(plan, savings, worstGapAge)),
        currentIncomeToday: grossIncomeTodayAtAge(profile, income, profile.currentAge),
        currentRequiredToday: requiredGrossTodayAtAge(
          plan,
          profile.currentAge,
          savingsBundleAtAge(plan, savings, profile.currentAge),
        ),
        currentFundedPct:
          requiredGrossTodayAtAge(
            plan,
            profile.currentAge,
            savingsBundleAtAge(plan, savings, profile.currentAge),
          ) > 0
            ? Math.min(
                100,
                (grossIncomeTodayAtAge(profile, income, profile.currentAge) /
                  requiredGrossTodayAtAge(
                    plan,
                    profile.currentAge,
                    savingsBundleAtAge(plan, savings, profile.currentAge),
                  )) *
                  100,
              )
            : 100,
        bottleneckFundedPct,
        peakRequiredAge: peakYearAge,
        peakRequiredToday: requiredGrossTodayAtAge(plan, peakYearAge, savingsBundleAtAge(plan, savings, peakYearAge)),
      }
    : undefined;

  return {
    peakRequiredGross,
    worstYearGap,
    peakYearAge,
    worstGapAge,
    phasedAffordability,
  };
}

export function computeHealthMonthlyReserve(
  health: FinancialPlanData['health'],
  yearsToRetire: number,
): number {
  const totalReserve =
    clampNonNeg(health.preMedicareMonthlyPremium) * 12 * clampNonNeg(health.yearsOfCoverageNeeded) +
    clampNonNeg(health.deductibleBufferTarget);
  const amortizeYears = Math.max(1, yearsToRetire || 1);
  return totalReserve / (amortizeYears * 12);
}

export function computeLifestyleMonthly(lifestyle: FinancialPlanData['lifestyle']): number {
  const vacations =
    clampNonNeg(lifestyle.vacationsPerYear) * clampNonNeg(lifestyle.vacationCost);
  const other = lifestyle.otherGoals.reduce((s, g) => s + clampNonNeg(g.annualAmount), 0);
  return (vacations + other) / 12;
}

function totalInvestmentContributions(accounts: FinancialPlanAccounts): number {
  return (Object.keys(accounts) as (keyof FinancialPlanAccounts)[]).reduce(
    (s, k) => s + clampNonNeg(accounts[k].monthlyContribution),
    0,
  );
}

function total529Contributions(children: Child529Plan[]): number {
  return children.reduce((s, c) => s + clampNonNeg(c.monthlyContribution529), 0);
}

/** How much of the required monthly savings pace you're already funding (0–100). */
function savingsPacePct(current: number, additionalNeeded: number): number {
  const target = current + additionalNeeded;
  if (target <= 0) return 100;
  return Math.min(100, (current / target) * 100);
}

function projectRetirementAccountsCurrentPath(plan: FinancialPlanData): number {
  const { profile, accounts } = plan;
  const years = yearsToRetirement(profile.currentAge, profile.retirementAge);
  const returnPct = profile.portfolioReturnPct;
  const zeroExtra = distributeExtraToAccounts(plan, 0);
  let balances = startingInvestmentBalances(accounts);
  for (let i = 0; i < years; i++) {
    const age = profile.currentAge + i;
    const baseMonthly = accountMonthlyContributionsAtAge(plan, age);
    balances = advanceInvestmentAccountsOneYear(balances, accounts, returnPct, zeroExtra, baseMonthly, age);
  }
  return totalInvestmentBalance(balances);
}

export function computeRequiredIncome(plan: FinancialPlanData): RequiredIncomeResult {
  const { profile, currentFinances, retirement, children, health, lifestyle, accounts, income } = plan;
  const years = yearsToRetirement(profile.currentAge, profile.retirementAge);
  const returnPct = profile.portfolioReturnPct;
  const taxRate = pctToRate(profile.effectiveTaxRatePct);

  const retirementCorpusNeeded = computeRetirementCorpus(profile, retirement);

  const accountKeys = Object.keys(accounts) as (keyof FinancialPlanAccounts)[];
  const retirementProjected = projectRetirementAccountsCurrentPath(plan);
  const accountProjections = accountKeys.map((key) => {
    const accountInput =
      key === 'k401' ? { ...accounts[key], employerMatchPct: 0 } : accounts[key];
    const projected = computeAccountProjection(accountInput, years, returnPct);
    const pctOfRetirementGoal =
      retirementCorpusNeeded > 0 ? Math.min(100, (projected / retirementCorpusNeeded) * 100) : 0;
    return {
      account: key,
      label: ACCOUNT_DISPLAY_LABELS[key],
      projectedBalance: projected,
      pctOfRetirementGoal,
    };
  });
  const retirementShortfall = Math.max(0, retirementCorpusNeeded - retirementProjected);
  const retirementMonthlySavings = years > 0 ? pmtMonthly(retirementShortfall, returnPct, years) : retirementShortfall;

  const collegeDetails = computeCollegeNeeds(children, profile.inflationPct, returnPct);
  const collegeMonthlySavings = collegeDetails.reduce((s, c) => s + c.monthlySavingsNeeded, 0);

  const k12Details = computeK12Needs(children, profile, returnPct);
  const k12MonthlySavings = k12Details.reduce((s, c) => s + c.monthlySavingsNeeded, 0);

  const healthMonthly = computeHealthMonthlyReserve(health, years);
  const lifestyleMonthly = computeLifestyleMonthly(lifestyle);
  const livingMonthly = livingMonthlyAtPlanAge(plan, profile.currentAge);

  const debtMonthlyToday = debtLedgerAtAge(plan.debts, profile, profile.currentAge).monthlyService;

  const constantSavingsBundle: MonthlySavingsBundle = {
    retirement: retirementMonthlySavings,
    college: collegeMonthlySavings,
    k12: k12MonthlySavings,
    health: healthMonthly,
    lifestyle: lifestyleMonthly,
  };

  const phasedActive = income.phases.length > 0;
  const savingsBundle = phasedActive
    ? computePathAdjustedSavingsBundle(plan, constantSavingsBundle, profile.currentAge)
    : constantSavingsBundle;

  const pathAdjustedRetirement = savingsBundle.retirement;
  const pathAdjustedCollege = savingsBundle.college;
  const pathAdjustedK12 = savingsBundle.k12;
  const pathAdjustedHealth = savingsBundle.health;
  const pathAdjustedLifestyle = savingsBundle.lifestyle;

  const totalSavingsNeeded =
    pathAdjustedRetirement +
    pathAdjustedCollege +
    pathAdjustedK12 +
    pathAdjustedHealth +
    pathAdjustedLifestyle;

  const requiredNetMonthly = livingMonthly + totalSavingsNeeded + debtMonthlyToday;
  const requiredGrossMonthly = taxRate < 1 ? requiredNetMonthly / (1 - taxRate) : requiredNetMonthly;
  const requiredGrossAnnual = requiredGrossMonthly * 12;

  const currentGrossAnnual = grossIncomeAtAge(profile, income, profile.currentAge);
  const takeHomeMonthly = (currentGrossAnnual / 12) * (1 - taxRate);
  const gapAnnual = requiredGrossAnnual - currentGrossAnnual;
  const gapMonthly = gapAnnual / 12;

  const fundedPct =
    requiredGrossAnnual > 0
      ? Math.min(100, (currentGrossAnnual / requiredGrossAnnual) * 100)
      : 100;

  const peakMetrics = computePeakYearMetrics(plan, constantSavingsBundle, requiredGrossAnnual);
  const phased = peakMetrics.phasedAffordability;
  const retirementFundedPctFromAccounts =
    retirementCorpusNeeded > 0
      ? Math.min(100, (retirementProjected / retirementCorpusNeeded) * 100)
      : 100;
  const displayFundedPct = phased ? retirementFundedPctFromAccounts : fundedPct;

  const retirementCurrent = totalInvestmentContributions(accounts);
  const collegeCurrent = total529Contributions(children);
  const healthCurrent = clampNonNeg(health.hsaAnnualContribution) / 12;

  const breakdown = [
    {
      id: 'living',
      label: 'Current living expenses',
      monthlyAmount: livingMonthly,
      annualAmount: livingMonthly * 12,
      category: 'living' as const,
      currentMonthly: livingMonthly,
      targetMonthly: livingMonthly,
      fundedPct: null,
      shareOfTakeHomePct:
        takeHomeMonthly > 0 ? Math.min(100, (livingMonthly / takeHomeMonthly) * 100) : 0,
    },
    {
      id: 'debt',
      label: 'Debt service',
      monthlyAmount: debtMonthlyToday,
      annualAmount: debtMonthlyToday * 12,
      category: 'debt' as const,
      currentMonthly: debtMonthlyToday,
      targetMonthly: debtMonthlyToday,
      fundedPct: null,
    },
    {
      id: 'retirement',
      label: 'Retirement savings',
      monthlyAmount: pathAdjustedRetirement,
      annualAmount: pathAdjustedRetirement * 12,
      category: 'retirement' as const,
      currentMonthly: retirementCurrent,
      targetMonthly: retirementCurrent + pathAdjustedRetirement,
      fundedPct: savingsPacePct(retirementCurrent, pathAdjustedRetirement),
    },
    {
      id: 'college',
      label: 'College / 529 savings',
      monthlyAmount: pathAdjustedCollege,
      annualAmount: pathAdjustedCollege * 12,
      category: 'college' as const,
      currentMonthly: collegeCurrent,
      targetMonthly: collegeCurrent + pathAdjustedCollege,
      fundedPct: savingsPacePct(collegeCurrent, pathAdjustedCollege),
    },
    {
      id: 'k12',
      label: 'K-12 schooling savings',
      monthlyAmount: pathAdjustedK12,
      annualAmount: pathAdjustedK12 * 12,
      category: 'k12' as const,
      currentMonthly: 0,
      targetMonthly: pathAdjustedK12,
      fundedPct: pathAdjustedK12 > 0 ? 0 : 100,
    },
    {
      id: 'health',
      label: 'Health & insurance reserve',
      monthlyAmount: pathAdjustedHealth,
      annualAmount: pathAdjustedHealth * 12,
      category: 'health' as const,
      currentMonthly: healthCurrent,
      targetMonthly: healthCurrent + pathAdjustedHealth,
      fundedPct: savingsPacePct(healthCurrent, pathAdjustedHealth),
    },
    {
      id: 'lifestyle',
      label: 'Lifestyle & discretionary',
      monthlyAmount: pathAdjustedLifestyle,
      annualAmount: pathAdjustedLifestyle * 12,
      category: 'lifestyle' as const,
      currentMonthly: 0,
      targetMonthly: pathAdjustedLifestyle,
      fundedPct: pathAdjustedLifestyle > 0 ? 0 : 100,
    },
  ].filter((b) => b.monthlyAmount > 0 || b.targetMonthly > 0);

  return {
    requiredGrossMonthly,
    requiredGrossAnnual,
    requiredNetMonthly,
    currentGrossAnnual,
    gapAnnual,
    gapMonthly,
    fundedPct: displayFundedPct,
    retirementCorpusNeeded,
    retirementProjected,
    takeHomeMonthly,
    peakRequiredGross: peakMetrics.peakRequiredGross,
    worstYearGap: peakMetrics.worstYearGap,
    peakYearAge: peakMetrics.peakYearAge,
    worstGapAge: peakMetrics.worstGapAge,
    phasedAffordability: peakMetrics.phasedAffordability,
    breakdown,
    collegeDetails,
    k12Details,
    accountProjections,
  };
}

function computeConstantSavingsBundle(plan: FinancialPlanData): MonthlySavingsBundle {
  const { profile, children, health, lifestyle, accounts, retirement } = plan;
  const years = yearsToRetirement(profile.currentAge, profile.retirementAge);
  const returnPct = profile.portfolioReturnPct;
  const retirementCorpusNeeded = computeRetirementCorpus(profile, retirement);
  const retirementProjected = projectRetirementAccountsCurrentPath(plan);
  const retirementShortfall = Math.max(0, retirementCorpusNeeded - retirementProjected);
  const retirementMonthlySavings =
    years > 0 ? pmtMonthly(retirementShortfall, returnPct, years) : retirementShortfall;
  const collegeDetails = computeCollegeNeeds(children, profile.inflationPct, returnPct);
  const collegeMonthlySavings = collegeDetails.reduce((s, c) => s + c.monthlySavingsNeeded, 0);
  const k12Details = computeK12Needs(children, profile, returnPct);
  const k12MonthlySavings = k12Details.reduce((s, c) => s + c.monthlySavingsNeeded, 0);
  const healthMonthly = computeHealthMonthlyReserve(health, years);
  const lifestyleMonthly = computeLifestyleMonthly(lifestyle);
  return {
    retirement: retirementMonthlySavings,
    college: collegeMonthlySavings,
    k12: k12MonthlySavings,
    health: healthMonthly,
    lifestyle: lifestyleMonthly,
  };
}

function planWithPhaseBaseIncome(
  plan: FinancialPlanData,
  phaseId: string,
  baseIncome: number,
): FinancialPlanData {
  return {
    ...plan,
    income: {
      ...plan.income,
      phases: plan.income.phases.map((p) =>
        p.id === phaseId ? { ...p, grossAnnualIncome: Math.max(0, baseIncome) } : p,
      ),
    },
  };
}

function phaseAgeRange(
  plan: FinancialPlanData,
  phase: IncomePhase,
): { fromAge: number; toAge: number } | null {
  const fromAge = Math.max(plan.profile.currentAge, phase.startAge);
  const toAge = Math.min(phase.endAge, plan.profile.retirementAge - 1);
  if (fromAge > toAge) return null;
  return { fromAge, toAge };
}

/** Every year in the phase has income ≥ required gross (other phases unchanged). */
function isPhaseLocallyAchievable(
  plan: FinancialPlanData,
  phaseId: string,
  baseIncome: number,
  constantSavings: MonthlySavingsBundle,
): boolean {
  const phase = plan.income.phases.find((p) => p.id === phaseId);
  if (!phase) return true;
  const range = phaseAgeRange(plan, phase);
  if (!range) return true;

  const modified = planWithPhaseBaseIncome(plan, phaseId, baseIncome);
  const { profile, income } = modified;
  for (let age = range.fromAge; age <= range.toAge; age++) {
    const savings = savingsBundleAtAge(modified, constantSavings, age);
    const required = requiredGrossTodayAtAge(modified, age, savings);
    const incomeToday = grossIncomeTodayAtAge(profile, income, age);
    if (incomeToday + 0.5 < required) return false;
  }
  return true;
}

function findMinimumPhaseBaseIncome(
  plan: FinancialPlanData,
  phaseId: string,
  constantSavings: MonthlySavingsBundle,
  peakRequiredToday: number,
): number | null {
  const phase = plan.income.phases.find((p) => p.id === phaseId);
  if (!phase) return 0;
  if (!phaseAgeRange(plan, phase)) return 0;

  const achievable = (base: number) =>
    isPhaseLocallyAchievable(plan, phaseId, base, constantSavings);

  let high = Math.max(clampNonNeg(phase.grossAnnualIncome), peakRequiredToday, 50_000);
  const maxSearch = 50_000_000;
  while (!achievable(high) && high < maxSearch) {
    high *= 2;
  }
  if (!achievable(high)) return null;

  let low = 0;
  while (high - low > 50) {
    const mid = (low + high) / 2;
    if (achievable(mid)) high = mid;
    else low = mid;
  }
  return Math.ceil(high / 50) * 50;
}

function peakRequiredInPhase(
  plan: FinancialPlanData,
  phase: IncomePhase,
  constantSavings: MonthlySavingsBundle,
): {
  peakRequiredToday: number;
  peakRequiredAge: number;
  peakPathAdjustedToday: number;
} {
  const { profile } = plan;
  const fromAge = Math.max(profile.currentAge, phase.startAge);
  const toAge = Math.min(phase.endAge, profile.retirementAge - 1);
  let peakRequiredToday = 0;
  let peakPathAdjustedToday = 0;
  let peakRequiredAge = fromAge;
  for (let age = fromAge; age <= toAge; age++) {
    const fullPlanToday = requiredGrossTodayAtAge(plan, age, constantSavings);
    const pathAdjustedToday = requiredGrossTodayAtAge(
      plan,
      age,
      savingsBundleAtAge(plan, constantSavings, age),
    );
    if (fullPlanToday > peakRequiredToday) {
      peakRequiredToday = fullPlanToday;
      peakRequiredAge = age;
    }
    peakPathAdjustedToday = Math.max(peakPathAdjustedToday, pathAdjustedToday);
  }
  return { peakRequiredToday, peakRequiredAge, peakPathAdjustedToday };
}

function tightestYearInPhase(
  plan: FinancialPlanData,
  phase: IncomePhase,
  constantSavings: MonthlySavingsBundle,
): {
  tightestYearAge: number;
  tightestYearPlannedToday: number;
  tightestYearRequiredToday: number;
  tightestYearMarginToday: number;
} {
  const { profile, income, currentFinances } = plan;
  const fromAge = Math.max(profile.currentAge, phase.startAge);
  const toAge = Math.min(phase.endAge, profile.retirementAge - 1);
  let tightestYearAge = fromAge;
  let tightestYearMarginToday = Number.POSITIVE_INFINITY;
  let tightestYearPlannedToday = 0;
  let tightestYearRequiredToday = 0;

  for (let age = fromAge; age <= toAge; age++) {
    const required = requiredGrossTodayAtAge(
      plan,
      age,
      savingsBundleAtAge(plan, constantSavings, age),
    );
    const planned = grossIncomeTodayAtAge(profile, income, age);
    const margin = planned - required;
    if (margin < tightestYearMarginToday) {
      tightestYearMarginToday = margin;
      tightestYearAge = age;
      tightestYearPlannedToday = planned;
      tightestYearRequiredToday = required;
    }
  }

  if (!Number.isFinite(tightestYearMarginToday)) tightestYearMarginToday = 0;
  return {
    tightestYearAge,
    tightestYearPlannedToday,
    tightestYearRequiredToday,
    tightestYearMarginToday,
  };
}

function phaseDisplayLabel(phase: IncomePhase, index: number): string {
  const trimmed = phase.label?.trim();
  if (trimmed) return trimmed;
  return `Phase ${index + 1} · ages ${phase.startAge}–${phase.endAge}`;
}

/** Independent recomputation helpers — used by calculation audit tests. */
export function auditPhasePeakAndTightest(
  plan: FinancialPlanData,
  phase: IncomePhase,
  constantSavings: ReturnType<typeof computeConstantSavingsBundle>,
) {
  return {
    peak: peakRequiredInPhase(plan, phase, constantSavings),
    tightest: tightestYearInPhase(plan, phase, constantSavings),
  };
}

export function auditConstantSavingsBundle(plan: FinancialPlanData) {
  return computeConstantSavingsBundle(plan);
}

/** Path-adjusted monthly savings need by category at a plan age (debug / audit). */
export function getSavingsBundleAtAge(plan: FinancialPlanData, age: number): MonthlySavingsBundle {
  const constant = computeConstantSavingsBundle(plan);
  return savingsBundleAtAge(plan, constant, age);
}

export function auditRequiredGrossTodayAtAge(
  plan: FinancialPlanData,
  age: number,
  usePathAdjusted: boolean,
) {
  const constant = computeConstantSavingsBundle(plan);
  const savings = usePathAdjusted ? savingsBundleAtAge(plan, constant, age) : constant;
  return requiredGrossTodayAtAge(plan, age, savings);
}

export function auditMinimumPhaseBase(
  plan: FinancialPlanData,
  phaseId: string,
  peakRequiredToday: number,
) {
  const constant = computeConstantSavingsBundle(plan);
  return findMinimumPhaseBaseIncome(plan, phaseId, constant, peakRequiredToday);
}

/** Minimum starting base income per phase (today's $) with other phases unchanged. */
export function computeIncomePhaseMinimums(plan: FinancialPlanData): IncomePhaseMinimumResult[] {
  const { profile, income } = plan;
  if (income.phases.length === 0) return [];

  const constantSavings = computeConstantSavingsBundle(plan);
  const sorted = [...income.phases].sort((a, b) => a.startAge - b.startAge);

  return sorted.map((phase, index) => {
    const fromAge = Math.max(profile.currentAge, phase.startAge);
    const overlapsAccumulation = fromAge < profile.retirementAge;
    const { peakRequiredToday, peakRequiredAge, peakPathAdjustedToday } = overlapsAccumulation
      ? peakRequiredInPhase(plan, phase, constantSavings)
      : { peakRequiredToday: 0, peakRequiredAge: phase.startAge, peakPathAdjustedToday: 0 };

    const tightest = overlapsAccumulation
      ? tightestYearInPhase(plan, phase, constantSavings)
      : {
          tightestYearAge: phase.startAge,
          tightestYearPlannedToday: 0,
          tightestYearRequiredToday: 0,
          tightestYearMarginToday: 0,
        };

    const minimumBase = overlapsAccumulation
      ? findMinimumPhaseBaseIncome(plan, phase.id, constantSavings, peakRequiredToday)
      : 0;
    const unreachable = minimumBase == null;
    const minimumBaseIncome = unreachable ? 0 : minimumBase;
    const currentBaseIncome = clampNonNeg(phase.grossAnnualIncome);
    const currentAnnualBonus = Math.max(0, phase.annualBonus ?? 0);
    const goalCarriedByEarlierPhases =
      !unreachable &&
      peakPathAdjustedToday > 0 &&
      minimumBaseIncome < peakPathAdjustedToday * 0.25;
    const meetsMinimum = !unreachable && currentBaseIncome + 0.5 >= minimumBaseIncome;
    const cushion = meetsMinimum ? currentBaseIncome - minimumBaseIncome : 0;

    return {
      phaseId: phase.id,
      label: phaseDisplayLabel(phase, index),
      startAge: phase.startAge,
      endAge: phase.endAge,
      isCurrentPhase: profile.currentAge >= phase.startAge && profile.currentAge <= phase.endAge,
      currentBaseIncome,
      currentAnnualBonus,
      minimumBaseIncome,
      goalCarriedByEarlierPhases,
      peakRequiredToday,
      peakRequiredAge,
      peakPathAdjustedToday,
      tightestYearAge: tightest.tightestYearAge,
      tightestYearPlannedToday: tightest.tightestYearPlannedToday,
      tightestYearRequiredToday: tightest.tightestYearRequiredToday,
      tightestYearMarginToday: tightest.tightestYearMarginToday,
      meetsMinimum,
      cushion,
      unreachable,
    };
  });
}

export function formatCurrency(value: number, decimals = 0): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatPct(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}
